import React, { useState, useEffect } from "react";
import { X, Download, ArrowUpRight, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function RequestPitchDeckModal({ open, onClose, deckId }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    name: "", email: "", company: "",
    investor_type: "institutional", investment_range: "5m_25m", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open && user && user !== false) {
      setForm((f) => ({ ...f, name: user.name || "", email: user.email || "", company: user.company || "" }));
    }
    if (!open) { setResult(null); }
  }, [open, user]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/pitch-deck/request", { ...form, deck_id: deckId || null });
      setResult(data);
      if (data.status === "approved") {
        toast.success("Pitch deck approved instantly.");
      } else {
        toast.success("Request submitted for review.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-background/90 backdrop-blur-md overflow-y-auto p-6 lg:p-12">
      <div className="max-w-xl mx-auto glass-strong p-8 lg:p-10" data-testid="request-deck-modal">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline mb-2">— Pitch Deck</div>
            <h2 className="font-serif text-3xl">Request access</h2>
          </div>
          <button onClick={onClose} data-testid="request-deck-close" className="p-2"><X className="w-5 h-5" /></button>
        </div>

        {!result ? (
          <>
            <p className="text-sm text-muted-foreground mb-6">
              {user && user !== false
                ? "You're signed in as a verified investor — your deck will be delivered instantly."
                : "Cold inquiries are reviewed by our investor relations team within two business days. Sign in for instant access."}
            </p>
            <form onSubmit={submit} data-testid="request-deck-form" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input data-testid="request-deck-name" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border border-input px-4 py-3" />
              <input data-testid="request-deck-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-background border border-input px-4 py-3" />
              <input data-testid="request-deck-company" placeholder="Firm / Family Office" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="md:col-span-2 bg-background border border-input px-4 py-3" />
              <select data-testid="request-deck-investor-type" value={form.investor_type} onChange={(e) => setForm({ ...form, investor_type: e.target.value })} className="bg-background border border-input px-4 py-3">
                <option value="individual">Individual / Accredited</option>
                <option value="institutional">Institutional Fund</option>
                <option value="family_office">Family Office</option>
                <option value="fund">REIT / Private Fund</option>
                <option value="developer">Developer / Strategic</option>
              </select>
              <select data-testid="request-deck-investment-range" value={form.investment_range} onChange={(e) => setForm({ ...form, investment_range: e.target.value })} className="bg-background border border-input px-4 py-3">
                <option value="under_1m">Under $1M</option>
                <option value="1m_5m">$1M – $5M</option>
                <option value="5m_25m">$5M – $25M</option>
                <option value="25m_100m">$25M – $100M</option>
                <option value="100m_plus">$100M+</option>
              </select>
              <textarea rows={3} placeholder="Message (optional)" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="md:col-span-2 bg-background border border-input px-4 py-3 resize-none" />
              <button data-testid="request-deck-submit" disabled={busy} className="md:col-span-2 group inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-6 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-all disabled:opacity-60">
                {busy ? "Submitting…" : user && user !== false ? "Get Instant Access" : "Submit Request"} <ArrowUpRight className="w-4 h-4" />
              </button>
            </form>
          </>
        ) : result.status === "approved" ? (
          <div data-testid="request-deck-approved">
            <div className="overline mb-2 text-primary">— Approved</div>
            <h3 className="font-serif text-2xl mb-4">Your deck is ready.</h3>
            <p className="text-sm text-muted-foreground mb-6">{result.deck_title}. Access expires {new Date(result.expires_at).toLocaleDateString()}.</p>
            <a data-testid="request-deck-download" href={`${BACKEND}${result.download_url}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-6 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors">
              <Download className="w-4 h-4" /> Download Pitch Deck
            </a>
          </div>
        ) : (
          <div data-testid="request-deck-pending">
            <div className="overline mb-2 text-primary">— Submitted</div>
            <h3 className="font-serif text-2xl mb-4">Thank you.</h3>
            <p className="text-sm text-muted-foreground mb-6">{result.message}</p>
            <button onClick={onClose} className="border border-primary/40 text-primary px-6 py-4 text-[12px] uppercase tracking-[0.22em] hover:bg-primary hover:text-primary-foreground transition-colors">Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
